/**
 * answer-view.js
 */

import React from 'react';
import { useMediaQuery, Typography, IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { parseISO, parse, format, intervalToDuration, formatDuration } from 'date-fns';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import DrawerContent from 'components/menu-drawer/drawer-content';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import IconSection from 'components/icon-section';
import InfoRow from 'components/info-row';
import Icon from 'components/icon';
import Button from 'components/button';
import EditButton from 'components/edit-button/edit-button';
import CaptionedSvg from 'components/captioned-svg';
import WeatherView from 'components/weather/view';
import ReviewToggle from 'components/review-toggle';
import TrainerCommentsDialog from 'components/trainer-comments-dialog';
import RejectionAlert from 'components/rejection-alert';
import OutdatedAlert from '../outdated-alert';
import OdorSummary from '../odor-summary';
import PatrolAgilityView from './patrol-agility-view';
import PatrolApprehensionView from './patrol-apprehension-view';
import PatrolBuildingSearchView from './patrol-building-search-view';
import PatrolEvidenceSearchView from './patrol-evidence-search-view';
import PatrolHumanSearchView from './patrol-human-search-view';
import PatrolObedienceView from './patrol-obedience-view';
import PatrolTrackingView from './patrol-tracking-view';
import PatrolOtherView from './patrol-other-view';
import FilesView from 'components/files/view/files-view';
import { ReactComponent as PassingBySvg } from 'assets/images/passing-by.svg';
import { ReactComponent as CompletedTasksSvg } from 'assets/images/completed-tasks.svg';
import { ReactComponent as BookingSvg } from 'assets/images/booking.svg';
import { ReactComponent as InNoTimeSvg } from 'assets/images/in-no-time.svg';
import { ReactComponent as SafeSvg } from 'assets/images/safe.svg';


const useStyles = makeStyles(theme => ({
  superActions: {
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(3),
  },
  cantComplete: {
    [theme.breakpoints.up('md')]: {
      width: '90%',
    },    
  },
  cantCompleteImgContainer: {
    marginBottom: theme.spacing(5),
  },
}));


// use wrapper to remount component when answer ID changes
export default function AnswerView(props) {
  const params = useParams();
  const answerId = params.answerId || 0;

  return <AnswerViewImplementation
    answerId={answerId}
    key={answerId} 
    {...props} 
  />;
}


function AnswerViewImplementation(props) {
  const {
    answerId,
    setDesktopActions,
    setMobileAppBar,
    drawerState,
    onClose,
    basePath,
  } = props;
  const classes = useStyles();
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), { noSsr: true });
  let history = useHistory();
  const recordStore = useRecordStore();
  const { user } = React.useContext(UserContext);
  const params = useParams();
  const exerciseId = params.exerciseId || 0;
  const handlerId = parseInt(params.handlerId) || 0;
  const answerRecord = recordStore.useRecord(Types.Record.ANSWER, answerId).ornull;
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId).ornull;
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, exerciseRecord?.get().eventId).ornull;
  const answer = answerRecord ? answerRecord.get() : null;
  const dogId = answerRecord ? answerRecord.get().dog.id : null;
  const hasRecord = Boolean(answer);

  const isComplete = Boolean(answer?.dateCompleted);
  const canComplete = answer?.canComplete;
  const dateCompleted = answer?.dateCompleted ? parseISO(answer?.dateCompleted) : null;
  const eventDate = parseISO(answer?.eventDate);
  const isFutureEvent = eventDate > new Date();
  const membership = user.role.isHandler
    ? eventRecord?.get()?.membership
    : eventRecord?.get()?.members.find(m => m.id === handlerId);


  if (user.role.status.isActive && canComplete) {
    history.replace(makeLocation(
      basePath,
      'training',
      'editAnswer',
      {exerciseId, answerId},
      history.location
    ));
  }
  
  
  // something is VERY weird with this file and i can't figure out what it is.
  // if the following useEffects and useCallbacks are placed after the stack of
  // definitions below the compiler errors about not conditionally calling them
  // implying that we are prematurely returning from the file somewhere when
  // WE MOST DEFINITELY ARE NOT FLUBBING DOING THAT.

  // i chased the error all over the file and was able to make it stop by commenting
  // out the most ridicuously irrelevant lines in various places.
  // sometimes adding an `|| true` to a random condition would trigger it while 
  // other times removing a single line in the render markup would make it stop.
  // but absolutely nothing sensical about it.
  
  // i dare you to try to figure it out without going insane.

  React.useEffect(() => {
    setTimeout(() => drawerState.loading.content.set(!hasRecord));

  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecord]);


  const onBack = React.useCallback(async () => {
    history.push(makeLocation(
      basePath,
      'training',
      'viewExercise',
      {exerciseId},
      history.location
    ));
  }, [history, basePath, exerciseId]);


  const onEdit = React.useCallback(() => {
    history.push(makeLocation(
      basePath,
      'training',
      'editAnswer',
      {exerciseId, answerId},
      history.location
    ));
  }, [history, basePath, exerciseId, answerId]);


  const editButton = React.useMemo(() => user.role.isHandler && !isFutureEvent 
    ? (<EditButton
      data-testid="edit-button"
      onClick={onEdit}
      disabled={!user.role.status.isActive}
      iconOnly={isMobileDrawer}
    />)
    : null, [user, onEdit, isFutureEvent, isMobileDrawer]);


  const backButton = React.useMemo(() => (
    <IconButton
      data-testid="back-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onBack}
      children={<Icon type='nav-back' />}
    />
  ), [onBack]);


  const closeButton = React.useMemo(() => (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
      children={<Icon type='mobile-close-drawer' />}
    />
  ), [onClose]);


  const supervisorActions = React.useMemo(() => user.role.isSupervisor ? (
    isComplete
      ? <>
        {/* <IconButton
          style={{ color: 'inherit', marginRight: -8 }}
          component={Link}
          href={`/Report/GenerateReport?OfficerId=${handlerId}&ExerciseId=${exerciseId}&DogId=${dogId}&ReportType=3&IsNewReport=True`}
        >
          <Icon type='report' />
        </IconButton> */}
        <ReviewToggle type={Types.Record.ANSWER} id={answerId} drawerState={drawerState} />
      </>
      : null
  
  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ) : null, [user, answerId, exerciseId, isComplete, handlerId, dogId]);


  const trainerComments = React.useMemo(() => user.role.isTrainer ? (
    <Tooltip
      arrow
      placement='top'
      title={!isComplete
        ? <>You cannot add trainer comments<br />to an incomplete exercise</>
        : 'Add trainer comments'}
    >
      <span>
        <TrainerCommentsDialog
          answerId={answerId}
          drawerState={drawerState}
          children={isMobileDrawer
            ? <IconButton
            data-testid="trainer-comments-button"
            style={isComplete ? {color: 'inherit'} : {}}
            disabled={!isComplete}
            children={<Icon type='trainer-comments' />}
            size="large" />
            : <Button
              data-testid="trainer-comments-button"
              variant='contained'
              disabled={!isComplete}
              startIcon={<Icon type='trainer-comments' />}
              children='Comments'
            />
          }
        />
      </span>
    </Tooltip>
  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ) : null, [user, answerId, isComplete]);


  React.useEffect(() => {
    if (setDesktopActions) {
      if (user.role.isHandler) {
        setDesktopActions(editButton);
      } else if (user.role.isSupervisor) {
        setDesktopActions(supervisorActions)
      } else if (user.role.isTrainer) {
        setDesktopActions(trainerComments);
      }
    }
    if (setMobileAppBar) {
      setMobileAppBar({
        title: 'Completion',
        leftActions: backButton,
        rightActions: <>{closeButton}{trainerComments}{editButton}</>,
        recordGroupType: Types.RecordGroup.TRAINING,
      });
    }
  }, [
    setDesktopActions, 
    setMobileAppBar,
    editButton, 
    backButton,
    closeButton,
    supervisorActions, 
    trainerComments, 
    user.role.isHandler, 
    user.role.isSupervisor, 
    user.role.isTrainer
  ]);


  const weather = answer?.weather;
  const showWeather = weather?.conditions || weather?.temperature || weather?.windDirection || weather?.windSpeed;
  
  
  // detection
  const detectionEnvironments = answer?.detection?.environments;
  const isBlind = answer?.detection?.blind || null;
  const monitor = answer?.detection?.monitor;
  const odorPlacementTime = answer?.detection?.odorPlacementTime;
  const detectionStartTime = answer?.detection?.startTime;
  const detectionEndTime = answer?.detection?.endTime;
  const detectionInterval = 
    detectionStartTime 
    && detectionEndTime 
    && intervalToDuration({ start: parseISO(detectionStartTime), end: parseISO(detectionEndTime) });
  const detectionDuration = 
    detectionInterval 
    && formatDuration(detectionInterval, {format: ['hours', 'minutes']});


  let cantCompleteReason;
  if (!answer.dateCompleted && (!user.role.status.isActive || !canComplete)) {
    cantCompleteReason = (() => {
      let reasonText, reasonImg;

      // HANDLER CAN'T COMPLETE REASONS
      if (user.role.isHandler) {
        if (!user.role.status.isActive) {
          reasonImg = <CaptionedSvg
            image={<SafeSvg />}
            text='You cannot complete this exercise because your account is read-only.'
          />;
        } else if (isFutureEvent) {
          reasonImg = <CaptionedSvg
            image={<InNoTimeSvg />}
            text='This exercise cannot be completed until the event starts.'
          />;
        } else if (membership.isOptional && membership.isAttending === null) {
          reasonImg = <CaptionedSvg
            image={<BookingSvg />}
            text='You cannot complete this exercise until you have confirmed that you are attending the event.'
          />;
          reasonText = <>
            If you wish to complete it now you may use the edit button above. Your status will be set to "Attending" automatically.
          </>;
        } else if (!answer.dog.appliesToExercise) {
          reasonImg = <CaptionedSvg
            image={<PassingBySvg />}
            text={`This exercise cannot be completed as it doesn't apply to ${answer.dog.name}.`}
          />;
          reasonText = <>
            If you'd like to make an exception and complete this exercise for {answer.dog.name} then use the edit button above to show the completion form.<br /><br />
            If this exercise type should always be applicable to {answer.dog.name} then modify their settings on the Dogs page to ensure that future exercises are correctly applied.
          </>;
        }
      }

      // SUPERVISOR NOT COMPLETE REASONS
      else if (user.role.isSupervisor || user.role.isTrainer) {
        if (membership.isOptional && membership.isAttending === null) {
          reasonImg = <CaptionedSvg
            image={<BookingSvg />}
            text='This handler has not confirmed that they are attending the event.'
          />;
        } else if (!answer.dog.appliesToExercise) {
          reasonImg = <CaptionedSvg
            image={<PassingBySvg />}
            text={`This exercise does not apply to ${answer.dog.name}.`}
          />;
        } else {
          reasonImg = <CaptionedSvg
            image={<CompletedTasksSvg />}
            text='This exercise has not been completed yet.'
          />;
        }
      }

      return {
        text: reasonText,
        img: reasonImg,
      };
    })();
  }



  const getPatrolTypeView = (patrolRecord) => {
    let Component = '';

    switch (patrolRecord.type) {
      case Types.Patrol.AGILITY:
        Component = PatrolAgilityView;
        break;

      case Types.Patrol.APPREHENSION:
        Component = PatrolApprehensionView;
        break;

      case Types.Patrol.BUILDING_SEARCH:
        Component = PatrolBuildingSearchView;
        break;

      case Types.Patrol.EVIDENCE_SEARCH:
        Component = PatrolEvidenceSearchView;
        break;

      case Types.Patrol.HUMAN_SEARCH:
        Component = PatrolHumanSearchView;
        break;

      case Types.Patrol.OBEDIENCE:
        Component = PatrolObedienceView;
        break;

      case Types.Patrol.TRACKING:
        Component = PatrolTrackingView;
        break;

      case Types.Patrol.OTHER:
        Component = PatrolOtherView;
        break;
        
      default:
        return '';
    }

    return <Component 
      key={patrolRecord.id} 
      record={patrolRecord} 
      patrolTypes={answer.patrol.patrolTypes}
      exerciseId={exerciseId}
      answerId={answerId} 
      dogId={dogId}
    />;
  }


  const getReviewedStatus = () => {
    const reviewer = answer?.review?.reviewer?.name;
    return answer.review.status === Types.ReviewStatus.ACCEPTED
      ? `Yes, by ${reviewer}`
      : answer.review.status === Types.ReviewStatus.REJECTED
        ? `Rejected, by ${reviewer}`
        : "No";
  }


  return (
    <>
      {answer &&
      <>
        <MobilePageTitle
          title={<>
            {exerciseRecord.get().name}
            &nbsp;&nbsp;&middot;&nbsp;&nbsp;
            {answer.dog.name.toLowerCase().includes('k9')
              ? answer.dog.name
              : `K9 ${answer.dog.name}`}
          </>}
        />

        <DrawerContent>
          {isComplete && user.role.isSupervisor && isMobileDrawer &&
          <div className={`flex-hc ${classes.superActions}`}>            
            {supervisorActions}
          </div>}

          {/* can't complete reason */}
          {cantCompleteReason &&
          <div className={classes.cantComplete}>
            {cantCompleteReason.img &&
            <div className={classes.cantCompleteImgContainer}>
              {cantCompleteReason.img}
            </div>
            }
            {cantCompleteReason.text &&
              <Typography variant='body1'>
                {cantCompleteReason.text}
              </Typography>
            }
          </div>
          }

          
          {/* completion view */}
          {Boolean(answer.dateCompleted) &&
          <>
            <RejectionAlert review={answer.review} />
            <OutdatedAlert answer={answer} exercise={exerciseRecord.get()} />

            {/* general info */}
            <IconSection iconName='summary'>
              <InfoRow title='Exercise Performed' text={answer.wasExercisePerformed ? 'Yes' : 'No'} />

              {/* not performed reason & comments */}
              {!answer.wasExercisePerformed &&
              <>
                <InfoRow title='Not Performed Reason' text={answer.notPerformedReasonString} />
                <InfoRow title='Not Performed Comments' text={answer.notPerformedComments} />
              </>}

              {/* general info */}
              {answer.wasExercisePerformed &&
              <>
                {dateCompleted > parse('2000-01-01', 'yyyy-MM-dd', new Date()) &&
                <InfoRow title='Saved On' text={format(dateCompleted, 'EEE, MMM dd, yyyy')} />}
                <InfoRow title='Report Reviewed' text={getReviewedStatus()} />
                {isBlind !== null &&
                <InfoRow title='Blind' text={isBlind ? 'Yes' : 'No'} />}
                {monitor &&
                <InfoRow title='Monitor' text={monitor} />}
              </>}
            </IconSection>

            {answer.wasExercisePerformed &&
            <>

              {/* detection */}
              {answer.detection !== null &&
              <>
                {/* environments + odors */}
                <IconSection iconName='environment'>
                  <Typography variant='subtitle2' style={{ marginBottom: 8 }}>
                    Odor Summary:
                  </Typography>
                  <OdorSummary detectionEnvironments={detectionEnvironments} />
                </IconSection>

                {/* times */}
                {(odorPlacementTime || detectionStartTime) &&
                <IconSection iconName='date'>
                  <Typography variant='subtitle2' style={{ marginBottom: 8 }}>
                    Time Settings:
                  </Typography>
                  
                  {odorPlacementTime && <InfoRow title='Odor Placement' text={format(parseISO(odorPlacementTime), 'MM/dd/yyyy h:mm aaa')} />}
                  {detectionStartTime &&
                  <InfoRow title='Detection Time'
                    text={`${format(parseISO(detectionStartTime), 'h:mm aaa')}` +
                          `${detectionEndTime ?' - ' : ''}` +
                          `${detectionEndTime ? format(parseISO(detectionEndTime), 'h:mm aaa') : ''}` +
                          `${detectionDuration ? ' (' + detectionDuration + ')' : ''}` }
                    />}
                </IconSection>}
              </>}


              {/* patrol */}
              {answer.patrol !== null &&
              <>
                {answer.patrol.patrolTypes.map(patrolRecord => getPatrolTypeView(patrolRecord))}
              </>}


              {/* weather */}
              {showWeather &&
              <WeatherView weather={weather} />}


              {/* comments */}
              {answer.comments &&
              <IconSection iconName='comment'>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {answer.comments}
                </Typography>
              </IconSection>}


              {/* files */}
              <FilesView files={answer.files} />


              {/* trainer comments */}
              {answer.trainerComments && answer.trainerComments.comments &&
              <IconSection iconName='trainer-comments'>
                <Typography style={{ fontWeight: 500, marginBottom: 8 }}>
                  Trainer {answer.trainerComments.trainer.name} provided the following comments:
                </Typography>
                <Typography variant='body2' style={{ whiteSpace: 'pre-wrap' }}>
                  {answer.trainerComments.comments}
                </Typography>
              </IconSection>}

            </>}
          </>}

        </DrawerContent>

      </>}
    </>
  );
}